import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SetupPortalPage from '../../components/setup/SetupPortalPage';
import BackToAgentSetupLink from '../../components/setup/BackToAgentSetupLink';
import WorkflowCard from '../../components/workflows/WorkflowCard';
import api from '../../API.js';
import styles from '../../styles/workflows.module.css';
import { useAuth } from '../../providers/AuthContext';
import { AGENT_SETUP_BASE } from '../../constants/setupRoadmapSteps';

function WorkflowManagement() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user && user.admin;
    
    const [workflows, setWorkflows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [executing, setExecuting] = useState(false);
    const [executionResult, setExecutionResult] = useState(null);
    const [executionProgress, setExecutionProgress] = useState(null);

    useEffect(() => {
        if (isAdmin) {
            loadWorkflows();
        }
    }, [isAdmin]);

    const loadWorkflows = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await api.get('workflow');
            
            if (response.status === 404) {
                setError('Workflow management is not available on this server.');
                return;
            }
            
            if (response.status !== 200) {
                throw new Error(`Failed to fetch workflows: ${response.status}`);
            }
            
            if (response.data.success) {
                setWorkflows(response.data.workflows || []);
            } else {
                setWorkflows([]);
            }
        } catch (err) {
            console.error('Error fetching workflows:', err);
            if (err.message.includes('404') || err.message.includes('Not Found')) {
                setError('Workflow management is not available on this server.');
            } else {
                setError('Failed to load workflows: ' + err.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteWorkflow = async (workflowId) => {
        if (!window.confirm(`Are you sure you want to delete workflow "${workflowId}"? This action cannot be undone.`)) {
            return;
        }

        try {
            const response = await api.delete(`workflow/${workflowId}`);
            if (response.status === 200) {
                await loadWorkflows();
            } else {
                throw new Error(`Failed to delete workflow: ${response.status}`);
            }
        } catch (err) {
            console.error('Error deleting workflow:', err);
            alert('Failed to delete workflow: ' + err.message);
        }
    };

    const handleExecuteWorkflow = async (workflowId) => {
        setExecuting(true);
        setError('');
        setExecutionResult(null);
        setExecutionProgress({ currentStep: 0, totalSteps: 0, stepName: 'Starting...' });

        try {
            const inputData = prompt('Enter input data (JSON format):', '{"query": "test"}');
            if (!inputData) {
                setExecuting(false);
                return;
            }

            const formData = new FormData();
            formData.append('input', inputData);

            const workflow = workflows.find(w => w.workflowId === workflowId);
            if (workflow) {
                setExecutionProgress({
                    currentStep: 0,
                    totalSteps: workflow.stepCount || 1,
                    stepName: 'Initializing...'
                });
            }

            const response = await api.post(`workflow/${workflowId}/execute`, {
                body: formData,
            });

            if (response.status === 200 && response.data.success) {
                setExecutionResult(response.data);
                if (response.data.stepResults) {
                    const stepResults = response.data.stepResults;
                    const stepNames = Object.keys(stepResults);
                    setExecutionProgress({
                        currentStep: stepNames.length,
                        totalSteps: stepNames.length,
                        stepName: 'Completed',
                        stepResults: stepResults
                    });
                }
            } else {
                throw new Error(response.data.error || 'Failed to execute workflow');
            }
        } catch (err) {
            console.error('Error executing workflow:', err);
            setError('Failed to execute workflow: ' + (err.message || err));
            setExecutionProgress(null);
        } finally {
            setExecuting(false);
        }
    };

    const filteredWorkflows = workflows.filter(workflow => {
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            return (
                workflow.name.toLowerCase().includes(searchLower) ||
                (workflow.description && workflow.description.toLowerCase().includes(searchLower)) ||
                workflow.workflowId.toLowerCase().includes(searchLower)
            );
        }
        return true;
    });

    if (!isAdmin) {
        return (
            <SetupPortalPage>
                <div>Access denied. Admin privileges required.</div>
            </SetupPortalPage>
        );
    }

    if (loading) {
        return (
            <SetupPortalPage>
                <div className={styles.loading}>Loading workflows...</div>
            </SetupPortalPage>
        );
    }

    return (
        <SetupPortalPage>
            <div className={styles.container}>
                <div style={{ marginBottom: '2em' }}>
                    <BackToAgentSetupLink />
                </div>

                <div className={styles.header}>
                    <h1>Workflow Management</h1>
                    <div className={styles.actions}>
                        <Link
                            to={`${AGENT_SETUP_BASE}/workflows/create`}
                            className={`${styles.button} ${styles.buttonPrimary}`}
                        >
                            Create Workflow
                        </Link>
                    </div>
                </div>

                {error && (
                    <div className={styles.error}>
                        {error}
                    </div>
                )}

                <div style={{ marginBottom: '1.5em' }}>
                    <input
                        type="text"
                        placeholder="Search workflows..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles.formInput}
                        style={{ maxWidth: '300px' }}
                    />
                </div>

                {filteredWorkflows.length === 0 ? (
                    <div className={styles.emptyState}>
                        <div className={styles.emptyStateIcon}>⚙️</div>
                        <div className={styles.emptyStateTitle}>
                            {searchTerm ? 'No workflows found' : 'No workflows yet'}
                        </div>
                        <div className={styles.emptyStateText}>
                            {searchTerm
                                ? 'Try adjusting your search terms'
                                : 'Create your first workflow to get started'}
                        </div>
                        {!searchTerm && (
                            <Link
                                to={`${AGENT_SETUP_BASE}/workflows/create`}
                                className={`${styles.button} ${styles.buttonPrimary}`}
                                style={{ marginTop: '1em', display: 'inline-block' }}
                            >
                                Create Your First Workflow
                            </Link>
                        )}
                    </div>
                ) : (
                    <div className={styles.cardsGrid}>
                        {filteredWorkflows.map(workflow => (
                            <WorkflowCard
                                key={workflow.workflowId}
                                workflow={workflow}
                                onDelete={handleDeleteWorkflow}
                                onExecute={handleExecuteWorkflow}
                            />
                        ))}
                    </div>
                )}

                {executing && executionProgress && (
                    <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#d1ecf1', borderRadius: '5px' }}>
                        <h3>Execution Progress</h3>
                        <div style={{ marginBottom: '10px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                <span>Step {executionProgress.currentStep} of {executionProgress.totalSteps}</span>
                                <span>{Math.round((executionProgress.currentStep / executionProgress.totalSteps) * 100)}%</span>
                            </div>
                            <div style={{
                                width: '100%',
                                height: '20px',
                                backgroundColor: '#e0e0e0',
                                borderRadius: '10px',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    width: `${(executionProgress.currentStep / executionProgress.totalSteps) * 100}%`,
                                    height: '100%',
                                    backgroundColor: '#007bff',
                                    transition: 'width 0.3s'
                                }}></div>
                            </div>
                        </div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                            Current: {executionProgress.stepName}
                        </div>
                    </div>
                )}

                {executionResult && (
                    <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#d4edda', borderRadius: '5px' }}>
                        <h3>Execution Result</h3>
                        {executionResult.stepResults && (
                            <div style={{ marginBottom: '15px' }}>
                                <h4>Step Results:</h4>
                                {Object.entries(executionResult.stepResults).map(([stepId, result]) => (
                                    <div key={stepId} style={{
                                        marginBottom: '10px',
                                        padding: '10px',
                                        backgroundColor: 'white',
                                        borderRadius: '3px',
                                        border: '1px solid #c3e6cb'
                                    }}>
                                        <strong>{stepId}</strong> ({result.stepType})
                                        {result.agentName && <span style={{ color: '#666', marginLeft: '10px' }}>Agent: {result.agentName}</span>}
                                        {result.error && (
                                            <div style={{ color: '#dc3545', marginTop: '5px' }}>
                                                Error: {result.error}
                                            </div>
                                        )}
                                        {result.output && (
                                            <div style={{ marginTop: '5px', fontSize: '12px', maxHeight: '100px', overflow: 'auto' }}>
                                                Output: {typeof result.output === 'string' ? result.output.substring(0, 200) : JSON.stringify(result.output).substring(0, 200)}...
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        <details>
                            <summary style={{ cursor: 'pointer', marginBottom: '10px' }}>Full Result (JSON)</summary>
                            <pre style={{ fontSize: '12px', overflow: 'auto', maxHeight: '300px' }}>
                                {JSON.stringify(executionResult, null, 2)}
                            </pre>
                        </details>
                    </div>
                )}
            </div>
        </SetupPortalPage>
    );
}

export default WorkflowManagement;
